import React, { useCallback, useEffect, useState } from "react";
import { Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, getData } from "actions";
import { API_URL_cannedResponse } from "constants/api";
import { cannedResponseReducers } from "redux/cannedResponseSlice";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { SyncLoader } from "react-spinners";
import ModalCannedResponse from "components/molecules/ModalCannedResponse";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "name" },
  { title: "Actions", field: "" },
];

const CannedResponses = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [itemModal, setItemModal] = useState(false);

  const {
    getCannedResponseResult,
    getCannedResponseLoading,
    getCannedResponseError,
    addCannedResponseResult,
    deleteCannedResponseResult,
  } = useSelector((state) => state.cannedResponse);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_cannedResponse,
        params,
        { dispatch, redux: cannedResponseReducers },
        "GET_CANNEDRESPONSE"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_cannedResponse + item.id,
      { dispatch, redux: cannedResponseReducers },
      "DELETE_CANNEDRESPONSE"
    );
  };

  const onEdit = (item) => {
    navigate(`/tools/cannedresponses/formcanned`, {
      state: {
        item,
      },
    });
  };

  const onView = (item) => {
    setIsOpen(true);
    setItemModal(item);
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "View",
      icon: <FaEye />,
      func: onView,
      colorlight: "bg-green-500",
      colordark: "dark:bg-green-800",
      hoverlight: "hover:bg-green-600",
      hoverdark: "hover:dark:bg-green-900",
    },
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addCannedResponseResult) {
      fetchData();
    }
  }, [addCannedResponseResult, fetchData]);

  useEffect(() => {
    if (deleteCannedResponseResult) {
      fetchData();
    }
  }, [deleteCannedResponseResult, fetchData]);

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <Container>
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12  w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
          Canned Responses
          <div className="col-end-7 flex justify-end">
            <Link
              to="/tools/cannedresponses/formcanned"
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create
            </Link>
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="m-5">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getCannedResponseLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getCannedResponseError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getCannedResponseError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getCannedResponseResult.results &&
                  getCannedResponseResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getCannedResponseResult.results &&
                  getCannedResponseResult.results.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b bg-gray-50 dark:bg-neutral-900"
                    >
                      <td className="p-2 border">
                        <button
                          className="text-blue-700 dark:text-blue-300"
                          onClick={() => onEdit(item)}
                        >
                          {item.title}
                        </button>
                      </td>
                      <td className="p-2 border text-black dark:text-white">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Modal */}
        <div>
          <ModalCannedResponse
            isOpen={isOpen}
            onClose={closeModal}
            item={itemModal}
          />
        </div>
      </div>
    </Container>
  );
};

export default CannedResponses;
